import { useCallback, useMemo } from 'react';
import {
    confirmModal,
    hideAppSpinner, showAppNotification, showAppSpinner,
} from '@rainbow-modules/app';
import Trash from 'components/icons/trash';
import ModalBody from 'components/ModalBody';
import useHttpMutation from 'data/firestore/useHttpMutation';

const useManageCustomers = ({
    agentId,
    onCustomerDeleted = () => {},
} : {
    agentId: string;
    onCustomerDeleted?: () => void;
}) => {
    const { mutateAsync: deleteCustomer } = useHttpMutation({
        method: 'DELETE',
    });

    const handleDeleteCustomer = useCallback(
        async (customerId: string) => {
            const result = await confirmModal({
                header: '',
                variant: 'destructive',
                question: (
                    <ModalBody
                        variant="destructive"
                        icon={<Trash />}
                        title="Remove customer?"
                        description="Once this action is confirmed, it cannot be undone."
                    />
                ),
                borderRadius: 'semi-square',
                okButtonLabel: 'Remove',
                cancelButtonLabel: 'Cancel',
            });

            if (!result) {
                return;
            }

            showAppSpinner();
            try {
                await deleteCustomer({
                    pathname: `/agents/${agentId}/customers/${customerId}`,
                });
                showAppNotification({
                    title: 'Success',
                    description: 'Phone deleted successfully.',
                    icon: 'success',
                    timeout: 5000,
                });
                onCustomerDeleted();
            } catch (error) {
                const message = (error as any)?.message || 'Something went wrong. Please try again.';
                showAppNotification({
                    title: 'Error',
                    description: message,
                    icon: 'error',
                    timeout: 5000,
                });
            }
            hideAppSpinner();
        },
        [deleteCustomer, agentId, onCustomerDeleted],
    );

    return useMemo(() => ({
        remove: handleDeleteCustomer,
    }), [
        handleDeleteCustomer,
    ]);
};

export default useManageCustomers;
